import { useCallback, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatAgo } from '@/lib/ago';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { SecurityBlock } from '@/types/security-block';

interface FormState {
  name: string;
  description: string;
  overlay_yaml: string;
  is_enabled_default: boolean;
  sort_order: number;
  is_active: boolean;
}

const emptyForm: FormState = {
  name: '',
  description: '',
  overlay_yaml: '',
  is_enabled_default: false,
  sort_order: 0,
  is_active: true,
};

function formatCreated(dateStr: string): { ago: string; full: string } | null {
  const t = Date.parse(dateStr);
  if (!dateStr || Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

/** Form target: null keeps the dialog closed; `null` id means create. */
type FormTarget = { mode: 'create' } | { mode: 'edit'; block: SecurityBlock } | null;

export function SecurityBlockList() {
  // API already sorts by sort_order; sort again so the table order never depends on the server.
  const blocks = usePolledData(async () => {
    const data = await api.securityBlocks.list();
    return [...data].sort((a, b) => a.sort_order - b.sort_order);
  });
  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<SecurityBlock | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteBusyRef = useRef(false);

  const runDelete = async () => {
    const target = deleting;
    if (!target || deleteBusyRef.current) return;
    deleteBusyRef.current = true;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.securityBlocks.delete(target.id);
      setDeleteOpen(false);
      toast.success('Security block deleted');
      blocks.refetch();
    } catch (err) {
      setDeleteError(err instanceof Error && err.message ? err.message : 'Failed to delete security block');
    } finally {
      deleteBusyRef.current = false;
      setDeleteBusy(false);
    }
  };

  const rows = blocks.data ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>Security blocks</CardTitle>
              <CardDescription>Reusable Compose overlays applied to stacks to enforce security baselines.</CardDescription>
            </div>
            <Button onClick={() => setFormTarget({ mode: 'create' })}>Create security block</Button>
          </div>
        </CardHeader>
        <CardContent data-focus-fallback tabIndex={-1} className="focus:outline-none">
          {blocks.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading security blocks…
            </p>
          ) : blocks.data === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{blocks.error ?? 'Failed to load security blocks'}</span>
              <Button size="sm" variant="outline" onClick={blocks.refetch}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No security blocks yet. Create one to enforce a baseline on stacks.
            </div>
          ) : (
            <>
              {blocks.error ? (
                <p role="alert" className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh security blocks: {blocks.error}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead>Sort</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((block) => {
                    const created = formatCreated(block.created_at);
                    return (
                      <TableRow key={block.id}>
                        <TableCell className="font-medium [overflow-wrap:anywhere]">{block.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{block.description || '-'}</TableCell>
                        <TableCell>
                          {block.is_enabled_default ? (
                            <StatusBadge status="active" label="On by default" />
                          ) : (
                            <StatusBadge status="neutral" label="Opt-in" />
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{block.sort_order}</TableCell>
                        <TableCell>
                          <StatusBadge status={block.is_active ? 'active' : 'revoked'} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {created ? (
                            <time dateTime={block.created_at} title={created.full}>
                              {created.ago}
                            </time>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button size="sm" variant="outline" aria-label={`Edit ${block.name}`} onClick={() => setFormTarget({ mode: 'edit', block })}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              aria-label={`Delete ${block.name}`}
                              onClick={() => {
                                setDeleteError(null);
                                setDeleting(block);
                                setDeleteOpen(true);
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <SecurityBlockFormDialog
        target={formTarget}
        onClose={() => setFormTarget(null)}
        onSaved={() => {
          setFormTarget(null);
          blocks.refetch();
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusyRef.current) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete security block?"
        description={
          deleting
            ? `Delete '${deleting.name}'? Stacks that use it will no longer receive this overlay on their next deploy.`
            : undefined
        }
        confirmLabel={deleteBusy ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />
    </>
  );
}

interface FormDialogProps {
  target: FormTarget;
  onClose: () => void;
  onSaved: () => void;
}

/** Shell always mounted; the form body mounts only while a target is set, so state resets on every open. */
function SecurityBlockFormDialog({ target, onClose, onSaved }: FormDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  const open = target !== null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {target ? (
        <FormBody
          key={target.mode === 'edit' ? `edit:${target.block.id}` : 'create'}
          target={target}
          onClose={onClose}
          onSaved={onSaved}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormBodyProps {
  target: NonNullable<FormTarget>;
  onClose: () => void;
  onSaved: () => void;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

function FormBody({ target, onClose, onSaved, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const editing = target.mode === 'edit' ? target.block : null;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [form, setForm] = useState<FormState>(() =>
    editing
      ? {
          name: editing.name,
          description: editing.description,
          overlay_yaml: editing.overlay_yaml,
          is_enabled_default: editing.is_enabled_default,
          sort_order: editing.sort_order,
          is_active: editing.is_active,
        }
      : emptyForm,
  );
  const [submitting, setSubmitting] = useState(false);
  // Server validation (for example a block that weakens the safety floor) is shown here, inside the form.
  const [error, setError] = useState<string | null>(null);

  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const base = {
        name: form.name,
        description: form.description || undefined,
        overlay_yaml: form.overlay_yaml,
        is_enabled_default: form.is_enabled_default,
        sort_order: form.sort_order,
      };
      if (editing) {
        await api.securityBlocks.update(editing.id, { ...base, is_active: form.is_active });
      } else {
        await api.securityBlocks.create(base);
      }
      toast.success(editing ? 'Security block saved' : 'Security block created');
      onSaved();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save security block');
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit security block' : 'Create security block'}</DialogTitle>
        <DialogDescription>
          {editing ? 'Update the overlay and defaults. The overlay is checked against the safety floor on save.' : 'Define a Compose overlay that enforces a security baseline on stacks.'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none [overflow-wrap:anywhere]">
            {error}
          </p>
        ) : null}

        <div className="space-y-2">
          <label htmlFor="sbName" className="text-sm font-medium">
            Name
          </label>
          <Input id="sbName" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="no-new-privileges" />
        </div>

        <div className="space-y-2">
          <label htmlFor="sbDescription" className="text-sm font-medium">
            Description
          </label>
          <Input
            id="sbDescription"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Prevents containers from gaining new privileges"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="sbOverlay" className="text-sm font-medium">
            Overlay YAML
          </label>
          <textarea
            id="sbOverlay"
            value={form.overlay_yaml}
            onChange={(e) => setForm({ ...form, overlay_yaml: e.target.value })}
            required
            spellCheck={false}
            placeholder={`security_opt:\n  - no-new-privileges:true`}
            className="min-h-[160px] w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="sbSort" className="text-sm font-medium">
            Sort order
          </label>
          <Input
            id="sbSort"
            type="number"
            min={0}
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={form.is_enabled_default}
            onChange={(e) => setForm({ ...form, is_enabled_default: e.target.checked })}
            className="size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <span className="text-sm font-medium">Enabled by default for new stacks</span>
        </label>

        {editing ? (
          <label className="flex cursor-pointer items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="text-sm font-medium">Active</span>
          </label>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button ref={cancelRef} type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
